import type { Format } from '@number-flow/react';
import NumberFlow from '@number-flow/react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { StatBlock, StatItem } from '@savvy-kit/patterns/stat-block';
import { CircleHelp } from 'lucide-react';

import { ARR_TOOLTIP_TEXT } from '@savvy/constants/content-strings/advisor-dashboard';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';

type AggregateStats = {
  readonly totalSavvyAum: string | null;
  readonly totalAdvisorAnnualRevenue: string | null;
  readonly clientsCount: number;
};

type ClientsTableCardsProps = {
  readonly aggregateStats: AggregateStats;
  readonly loading?: boolean;
  readonly hideArr?: boolean;
};

type StatCard = {
  label: string;
  value: number;
  format: Format;
  tooltip?: string;
};

const DOLLAR_FORMAT: Format = { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 };
const COUNT_FORMAT: Format = {};

export function ClientsTableCards({ aggregateStats, loading = false, hideArr = false }: ClientsTableCardsProps) {
  const { user } = useAuth();

  const cards: StatCard[] = [
    {
      label: 'Total AUM',
      value: Number(aggregateStats.totalSavvyAum ?? 0),
      format: DOLLAR_FORMAT,
    },
    ...(user?.role !== 'ASSISTANT' && !hideArr
      ? [
          {
            label: 'Total ARR',
            value: Number(aggregateStats.totalAdvisorAnnualRevenue ?? 0),
            format: DOLLAR_FORMAT,
            tooltip: ARR_TOOLTIP_TEXT,
          },
        ]
      : []),
    {
      label: 'Clients',
      value: aggregateStats.clientsCount,
      format: COUNT_FORMAT,
    },
  ];

  return (
    <StatBlock>
      {cards.map((card) => {
        // These totals come from the filtered clients connection, so a zero reads as
        // "nothing to total" rather than a real figure — hence N/A over a bare 0.
        const isEmpty = !loading && card.value === 0;

        return (
          <StatItem
            key={card.label}
            label={
              card.tooltip ? (
                <>
                  {card.label}
                  <Tooltip delay={300}>
                    <TooltipTrigger aria-label={`About ${card.label}`}>
                      <CircleHelp aria-hidden />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">{card.tooltip}</TooltipContent>
                  </Tooltip>
                </>
              ) : (
                card.label
              )
            }
            ariaLabel={card.label}
            value={<NumberFlow value={card.value} format={card.format} />}
            empty={isEmpty}
            emptyMessage="N/A"
            loading={loading}
            className={isEmpty ? 'opacity-45' : undefined}
          />
        );
      })}
    </StatBlock>
  );
}
